import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { getActiveOverviewTab, setActiveOverviewTab } from 'redux/ModelsSlice';

import { TabType } from '../types';
import { DEFAULT_MODEL_TAB_URL, getTabFromUrlValue, getUrlValueFromTab } from '../constants';

const useActiveTab = (initialTab: TabType = 'dashboard') => {
  const dispatch: any = useDispatch();
  const storedTab = useSelector(getActiveOverviewTab) as TabType | undefined;
  const location = useLocation();
  const navigate = useNavigate();
  const { modelId, tab } = useParams();
  const tabFromUrl = getTabFromUrlValue(tab ?? null);
  const activeTab = tabFromUrl ?? storedTab ?? initialTab;

  useEffect(() => {
    if (tabFromUrl) {
      const searchParameters = new URLSearchParams(location.search);
      const isDropdownDeepLink =
        searchParameters.has('dimensionId') ||
        searchParameters.has('fileId') ||
        searchParameters.has('pageId');

      if (isDropdownDeepLink) {
        if (storedTab !== tabFromUrl) {
          dispatch(setActiveOverviewTab(tabFromUrl));
        }
        return;
      }

      // Deep links should be respected: accept URL tab and hydrate Redux from it.
      if (storedTab !== tabFromUrl) {
        dispatch(setActiveOverviewTab(tabFromUrl));
      }
      return;
    }

    const fallbackTab = storedTab ?? initialTab;
    const fallbackUrlValue = getUrlValueFromTab(fallbackTab);
    if (modelId) {
      navigate(`/model/${modelId}/${fallbackUrlValue || DEFAULT_MODEL_TAB_URL}`, { replace: true });
    }

    if (!storedTab) {
      dispatch(setActiveOverviewTab(fallbackTab));
    }
  }, [dispatch, initialTab, location.search, modelId, navigate, storedTab, tabFromUrl]);

  const handleTabChange = useCallback(
    (nextTab: TabType) => {
      const nextUrlValue = getUrlValueFromTab(nextTab);
      if (modelId && tab === nextUrlValue) {
        if (storedTab !== nextTab) {
          dispatch(setActiveOverviewTab(nextTab));
        }
        return;
      }
      dispatch(setActiveOverviewTab(nextTab));
      if (modelId) {
        navigate(`/model/${modelId}/${nextUrlValue}`, { replace: false });
      }
    },
    [dispatch, modelId, navigate, storedTab, tab],
  );

  return {
    activeTab,
    setActiveTab: handleTabChange,
  };
};

export default useActiveTab;
